import { ReactNode } from 'react';
import {
  Button,
  ButtonProps,
  Text,
  Modal,
  ModalProps,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@cdl/heartbeat-react';

type Props = {
  title: string | ReactNode;
  isOpen: boolean;
  onClose: () => void;
  closeButtonLabel?: string;
  closeButtonRightIcon?: ButtonProps['rightIcon'] | null;
  onConfirm?: () => void;
  confirmButtonLabel?: string;
  confirmButtonRightIcon?: ButtonProps['rightIcon'] | null;
  size?: ModalProps['size'];
  withDashedBorder?: boolean;
  isBlocking?: boolean;
  children: ReactNode;
};

export const InfoModal = ({
  isOpen,
  onClose,
  closeButtonLabel = 'OK',
  closeButtonRightIcon,
  onConfirm,
  confirmButtonLabel = 'OK',
  confirmButtonRightIcon,
  title,
  children,
  size = '2xl',
  withDashedBorder = false,
  isBlocking = false,
}: Props) => {
  const baseCloseButtonProps = {
    onClick: onClose,
    size: 'xs',
    mr: 's',
    ...(closeButtonRightIcon && { rightIcon: closeButtonRightIcon }),
  };
  const getFooter = (): ReactNode =>
    onConfirm ? (
      <>
        <Button {...baseCloseButtonProps} variant='secondary'>
          {closeButtonLabel}
        </Button>
        <Button
          onClick={onConfirm}
          size='xs'
          {...(confirmButtonRightIcon && {
            rightIcon: confirmButtonRightIcon,
          })}
        >
          {confirmButtonLabel}
        </Button>
      </>
    ) : (
      <Button
        {...baseCloseButtonProps}
        {...(isBlocking && { variant: 'warning' })}
      >
        {closeButtonLabel}
      </Button>
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      size={size}
    >
      <ModalOverlay backgroundColor='rgba(8, 36, 76, 0.3)' />
      <ModalContent p='1.5rem 2rem'>
        <ModalHeader p='0 0 1rem 0'>
          <Text textStyle='section-title'>{title}</Text>
        </ModalHeader>
        {!isBlocking && <ModalCloseButton />}
        <ModalBody
          p='0.625rem'
          borderRadius='.25rem'
          {...(withDashedBorder && {
            border: '1px dashed',
            borderColor: 'primary.500',
          })}
        >
          {children}
        </ModalBody>
        <ModalFooter p='1rem 0 0 0'>{getFooter()}</ModalFooter>
      </ModalContent>
    </Modal>
  );
};
